"use client";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserById, fetchUserPicture } from "../../store/thunks/userThunk";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { PROFILE_PLACEHOLDER } from "../../utils/placeholders";
import {
  CalendarIcon,
  DocumentTextIcon,
  CheckIcon,
  ClockIcon,
  CogIcon,
  UserIcon,
} from "@heroicons/react/20/solid";
import { format } from "date-fns";
import { updateAppointment } from "../../store/thunks/appointmentsThunk";
import { toast } from "react-toastify";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function StepsVerticalListV2({ appointment }) {
  const dispatch = useDispatch();
  const selectedAppointment = useSelector(
    (state) => state.appointments.selectedAppointment || appointment
  );
  const userProfilePicture = useSelector((state) => state.users.profilePicture);
  const user = useSelector((state) => state.auth.user);
  // console.log("user", user);

  const [historyWithUsers, setHistoryWithUsers] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [pictureCache, setPictureCache] = useState({});
  const [failedPictureCache, setFailedPictureCache] = useState(new Set());
  const [userCache, setUserCache] = useState({});

  // console.log("pictureCache", pictureCache);

  const fetchUserDetails = async (userId) => {
    // Check if user details are already in cache
    if (userCache[userId]) {
      return userCache[userId];
    }

    // If not in cache, fetch and store them
    try {
      const userDetails = await dispatch(fetchUserById(userId)).unwrap();
      
      // Update cache with new user details
      setUserCache((prev) => ({
        ...prev,
        [userId]: userDetails,
      }));
      return userDetails;
    } catch (error) {
      console.error(`[Activity Timeline] Error fetching user details for ID ${userId}:`, error);
      return null;
    }
  };

  const fetchPicture = async (userId) => {
    // Check if picture is already in cache
    if (pictureCache[userId]) {
      return pictureCache[userId];
    }

    // Check if this user's picture has previously failed to load
    if (failedPictureCache.has(userId)) {
      console.log(`[Activity Timeline] Skipping profile picture fetch for user ${userId} - previously failed`);
      return PROFILE_PLACEHOLDER;
    }

    // If not in cache, fetch and store it
    try {
      const result = await dispatch(fetchUserPicture({ userId }));
      const pictureUrl = result.payload;
      
      if (pictureUrl === null || result.meta.rejectedWithValue) {
        // Picture doesn't exist or failed to load - cache this failure
        setFailedPictureCache((prev) => new Set([...prev, userId]));
        const placeholder = PROFILE_PLACEHOLDER;
        setPictureCache((prev) => ({
          ...prev,
          [userId]: placeholder,
        }));
        return placeholder;
      }

      // Successfully loaded picture
      setPictureCache((prev) => ({
        ...prev,
        [userId]: pictureUrl,
      }));
      return pictureUrl;
    } catch (error) {
      console.log(`[Activity Timeline] Profile picture fetch failed for user ${userId}, using placeholder`);
      setFailedPictureCache((prev) => new Set([...prev, userId]));
      const placeholder = PROFILE_PLACEHOLDER;
      setPictureCache((prev) => ({
        ...prev,
        [userId]: placeholder,
      }));
      return placeholder;
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      if (!selectedAppointment?.history || selectedAppointment.history.length === 0) {
        setHistoryWithUsers([]);
        return;
      }

      console.log('[Activity Timeline] Fetching user details for history entries...');
      
      // First group the consecutive events
      const groupedHistory = groupConsecutiveEvents(selectedAppointment.history);
      
      const updatedHistory = await Promise.all(
        groupedHistory.map(async (entry) => {
          // console.log("in stepsVerticalList, this is the entry", entry);
          if (entry.user && typeof entry.user === "string") {
            const userDetails = await fetchUserDetails(entry.user);
            if (userDetails) {
              let profilePicture = await fetchPicture(entry.user);
              if (!profilePicture) {
                profilePicture = PROFILE_PLACEHOLDER;
              }
              // console.log("profilePicture", profilePicture);
              return {
                ...entry,
                user: userDetails,
                profilePicture,
              };
            }
          }
          return entry;
        })
      );
      setHistoryWithUsers(updatedHistory);
    };

    fetchUsers();
  }, [selectedAppointment?.history?.length, selectedAppointment?._id]);

  function formatDate(timestamp) {
    return format(new Date(timestamp), "eee MMM dd, yyyy, HH:mm");
  }

  // Group consecutive email opens and other similar events
  const groupConsecutiveEvents = (history) => {
    if (!history || history.length === 0) return [];
    
    const sortedHistory = [...history].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const groupedHistory = [];
    
    for (let i = 0; i < sortedHistory.length; i++) {
      const current = sortedHistory[i];
      
      // Check if this is an email open event
      if (current.status === "Reminder Email Opened") {
        // Look ahead to see if there are more email opens before a significant event
        const emailOpens = [current];
        let j = i + 1;
        
        // Collect consecutive email opens or until we hit a significant event
        while (j < sortedHistory.length) {
          const next = sortedHistory[j];
          
          // Stop grouping if we hit a significant user action
          if (next.status === "Confirmed" || 
              next.status === "Treated" || 
              next.status === "Cancelled" ||
              next.status === "Booked" ||
              next.status === "Comments" ||
              (next.user && next.status !== "Reminder Email Opened")) {
            break;
          }
          
          if (next.status === "Reminder Email Opened") {
            emailOpens.push(next);
          }
          j++;
        }
        
        if (emailOpens.length > 1) {
          // Create a grouped entry
          const firstOpen = emailOpens[0];
          const lastOpen = emailOpens[emailOpens.length - 1];
          
          groupedHistory.push({
            ...firstOpen,
            isGrouped: true,
            groupCount: emailOpens.length,
            lastTimestamp: lastOpen.timestamp,
            comments: `Client opened appointment reminder email (${emailOpens.length} times)`,
            groupedEvents: emailOpens // Keep original events for reference
          });
          
          // Skip the processed items
          i = i + emailOpens.length - 1;
        } else {
          // Single email open, keep as is
          groupedHistory.push(current);
        }
      } else {
        // Non-email events, keep as is
        groupedHistory.push(current);
      }
    }
    
    return groupedHistory;
  };

  const handleSaveComment = async () => {
    console.log("newComment", newComment);
    if (!newComment.trim()) return;

    const updatedHistory = [
      ...selectedAppointment.history,
      {
        status: "Comments",
        comments: newComment,
        timestamp: new Date().toISOString(),
        user: user._id,
      },
    ];

    const updatedAppointment = {
      ...selectedAppointment,
      history: updatedHistory,
    };

    console.log("History with users", historyWithUsers);

    try {
      await dispatch(
        updateAppointment({
          id: selectedAppointment._id,
          updatedData: updatedAppointment,
        })
      ).unwrap();
      setNewComment("");
      toast.success("Comment added successfully");
    } catch (error) {
      console.log("Comment failed to add", error);
      toast.error("Failed to add comment");
    }
  };

  return (
    <div className="min-h-[300px] max-h-fit overflow-y-auto">
      <h3 className="text-sm font-medium text-gray-900 mb-3">Activity Timeline</h3>

      <div className="flex-1 overflow-y-auto">
        <ul className="space-y-2">
          {historyWithUsers.map((activityItem, activityItemIdx) => (
            <li
              key={activityItem.timestamp + "-" + activityItemIdx}
              className="relative flex gap-x-2">
              <div
                className={classNames(
                  activityItemIdx === historyWithUsers.length - 1
                    ? "h-5"
                    : "-bottom-5",
                  "absolute left-0 top-0 flex w-5 justify-center"
                )}>
                <div className="w-px bg-gray-200" />
              </div>
              {activityItem.status === "Comments" ? (
                <>
                  <img
                    alt=""
                    src={activityItem.profilePicture}
                    className="relative mt-1.5 h-5 w-5 flex-none rounded-full border border-white ring-1 ring-gray-200"
                  />
                  <div className="flex-1 rounded-sm border border-gray-100/50 bg-white p-2 shadow-sm hover:shadow-md transition-all">
                    <div className="flex justify-between gap-x-2">
                      <div className="text-xs leading-5 text-gray-500">
                        <span className="font-medium text-gray-900">
                          {activityItem.user?.firstName} {activityItem.user?.lastName}
                        </span>{" "}
                        commented
                      </div>
                      <time
                        dateTime={activityItem.timestamp}
                        className="flex-none text-xs leading-5 text-gray-500">
                        {formatDate(activityItem.timestamp)}
                      </time>
                    </div>
                    <p className="mt-0.5 text-xs leading-5 text-gray-700">
                      {activityItem.comments}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="relative flex h-5 w-5 flex-none items-center justify-center">
                    {activityItem.status === "Paid" ? (
                      <CheckCircleIcon
                        className="h-4 w-4 text-emerald-500"
                        aria-hidden="true"
                      />
                    ) : activityItem.status === "Booked" ? (
                      <CalendarIcon
                        className="h-4 w-4 text-blue-500"
                        aria-hidden="true"
                      />
                    ) : activityItem.status === "Invoiced" ? (
                      <DocumentTextIcon
                        className="h-4 w-4 text-amber-500"
                        aria-hidden="true"
                      />
                    ) : activityItem.status === "Invoice Paid" ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                        className="h-4 w-4 text-green-500">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.15 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z" />
                      </svg>
                    ) : activityItem.status === "Confirmed" ? (
                      <CheckIcon
                        className="h-4 w-4 text-emerald-500"
                        aria-hidden="true"
                      />
                    ) : activityItem.status === "Tentative" ? (
                      <ClockIcon
                        className="h-4 w-4 text-orange-500"
                        aria-hidden="true"
                      />
                    ) : activityItem.status === "Treated" ? (
                      <CheckCircleIcon
                        className="h-4 w-4 text-purple-500"
                        aria-hidden="true"
                      />
                    ) : activityItem.status === "Reminder Email Opened" ||
                      activityItem.status === "Invoice Email Opened" ? (
                      <UserIcon
                        className="h-4 w-4 text-blue-500"
                        aria-hidden="true"
                      />
                    ) : activityItem.status === "Payment Posted to QuickBooks" ||
                      activityItem.status === "Invoice Posted to QuickBooks" ||
                      activityItem.status === "SMS Reminder Sent" ||
                      activityItem.status === "Reminder SMS Sent" ||
                      activityItem.status === "SMS Reminder Failed" ||
                      activityItem.status === "SMS Reminder Skipped" ||
                      activityItem.status === "Email Reminder Sent" ||
                      activityItem.status === "Email Reminder Failed" ||
                      activityItem.status === "Email Reminder Skipped" ||
                      activityItem.status === "Reminder Email Delivered" ||
                      activityItem.status === "Reminder Email Sent" ||
                      activityItem.status === "Invoice Email Sent" ||
                      activityItem.status === "Invoice Email Failed" ||
                      activityItem.status === "Invoice Email Delivered" ? (
                      <CogIcon
                        className="h-4 w-4 text-gray-500"
                        aria-hidden="true"
                      />
                    ) : (
                      <div className="h-1.5 w-1.5 rounded-full bg-gray-200 ring-1 ring-gray-300" />
                    )}
                  </div>
                  <div className="flex-1 py-0.5">
                    <div className="flex flex-col text-xs leading-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-x-2">
                          {activityItem.status === "Payment Posted to QuickBooks" ||
                           activityItem.status === "Invoice Posted to QuickBooks" ||
                           activityItem.status === "SMS Reminder Sent" ||
                           activityItem.status === "Reminder SMS Sent" ||
                           activityItem.status === "SMS Reminder Failed" ||
                           activityItem.status === "SMS Reminder Skipped" ||
                           activityItem.status === "Email Reminder Sent" ||
                           activityItem.status === "Email Reminder Failed" ||
                           activityItem.status === "Email Reminder Skipped" ||
                           activityItem.status === "Reminder Email Delivered" ||
                           activityItem.status === "Reminder Email Sent" ||
                           activityItem.status === "Invoice Email Sent" ||
                           activityItem.status === "Invoice Email Failed" ||
                           activityItem.status === "Invoice Email Delivered" ? (
                            <span className="font-medium text-gray-900 text-xs">
                              System
                            </span>
                          ) : activityItem.status === "Reminder Email Opened" ||
                            activityItem.status === "Invoice Email Opened" ? (
                            <span className="font-medium text-gray-900 text-xs">
                              Client
                            </span>
                          ) : (
                            <span className="font-medium text-gray-900 text-xs">
                              {activityItem.user?.firstName}{" "}
                              {activityItem.user?.lastName}
                            </span>
                          )}
                        </div>
                        <time
                          dateTime={activityItem.dateTime}
                          className="text-xs text-gray-500">
                          {formatDate(activityItem.isGrouped ? activityItem.lastTimestamp : activityItem.timestamp)}
                        </time>
                      </div>
                      <span className="text-gray-500 text-xs mt-0.5">
                        {(() => {
                          switch (activityItem.status) {
                            case "Booked":
                              return `booked the appointment.`;
                            case "Tentative":
                              return "marked the appointment as tentative.";
                            case "Confirmed":
                              if (
                                activityItem.comments?.includes(
                                  "client via email"
                                )
                              ) {
                                return "Client confirmed the appointment via email.";
                              } else if (activityItem.user) {
                                return "confirmed the appointment.";
                              } else {
                                return "Appointment was confirmed.";
                              }
                            case "Treated":
                              return (
                                <>
                                  treated the appointment.
                                  {activityItem.comments && (
                                    <div className="mt-1 text-xs text-gray-600">
                                      {activityItem.comments
                                        .split(". ")
                                        .map((sentence, index) => (
                                          <div
                                            key={index}
                                            className="leading-snug">
                                            {sentence}.
                                          </div>
                                        ))}
                                    </div>
                                  )}
                                </>
                              );
                            case "Invoice Paid":
                              return `${activityItem.comments}`;
                            case "Soap Notes Saved":
                              return "saved a SOAP note.";
                            case "Soap Notes Updated":
                              return "updated a SOAP note.";
                            case "Soap Notes Completed":
                              return "completed a SOAP note.";
                            case "Soap Notes Approved":
                              return "approved a SOAP note.";
                            case "Invoice Posted to QuickBooks":
                              return "posted invoice to QuickBooks";
                            case "Cancelled - No Fee":
                              return "cancelled the appointment - No Fee";
                            case "Cancelled - Charge Fee":
                              return "cancelled the appointment - Charge Fee";
                            case "No Show - No Fee":
                              return "marked the appointment as No Show - No Fee";
                            case "No Show - Charge Fee":
                              return "marked the appointment as No Show - Charge Fee";
                            case "Payment Posted to QuickBooks":
                              return "posted payment to QuickBooks";
                            case "SMS Reminder Sent":
                              return "sent a SMS reminder";
                            case "Reminder SMS Sent":
                              return "sent a SMS reminder";
                            case "SMS Reminder Failed":
                              return "failed to send a SMS reminder";
                            case "SMS Reminder Skipped":
                              return "skipped sending a SMS reminder";
                            case "Email Reminder Sent":
                              return "sent an email reminder";
                            case "Email Reminder Failed":
                              return "failed to send an email reminder";
                            case "Email Reminder Skipped":
                              return "skipped sending an email reminder";
                            case "Reminder Email Opened":
                              // Check if the comment includes count information
                              if (activityItem.comments && activityItem.comments.includes('times)')) {
                                return activityItem.comments.replace('Client opened appointment reminder email', 'opened the appointment reminder email');
                              }
                              return "opened the appointment reminder email";
                            case "Reminder Email Delivered":
                              return "delivered the appointment reminder email";
                            case "Reminder Email Sent":
                              return "sent the appointment reminder email";
                            case "Invoice Email Sent":
                              return "sent the invoice via email";
                            case "Invoice Email Failed":
                              return "failed to send the invoice email";
                            case "Invoice Email Delivered":
                              return "delivered the invoice email";
                            case "Invoice Email Opened":
                              return "opened the invoice email";
                            default:
                              return `${activityItem.status} the appointment. ${activityItem.comments}`;
                          }
                        })()}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* New comment form */}
      <div className="pt-3 relative flex-auto">
        <div className="relative overflow-hidden rounded-sm border border-gray-200 shadow-sm focus-within:border-gray-950 focus-within:ring-1 focus-within:ring-gray-950 focus-within:ring-offset-1">
          <label htmlFor="comment" className="sr-only">
            Add your comment
          </label>
          <textarea
            id="comment"
            name="comment"
            rows={2}
            placeholder="Add your comment..."
            className="block w-full resize-none border-0 bg-transparent py-2 px-2.5 text-xs text-gray-900 placeholder:text-gray-400 focus:ring-0 focus:outline-none"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
        </div>

        {/* Comment Button */}
        <div className="flex justify-end pt-2">
          <button
            onClick={handleSaveComment}
            className="flex items-center gap-2 rounded-sm bg-gray-900/5 border border-gray-900/10 px-3 py-1.5 text-xs font-medium text-gray-900 shadow-sm transition-all hover:bg-gray-900/10 hover:shadow-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50">
            Comment
          </button>
        </div>
      </div>
    </div>
  );
}
